import React, { useState, useRef, useEffect } from 'react';
import { Check } from 'lucide-react';

interface StatusLabel {
  id: string;
  text: string;
  color: string;
  description?: string;
  isDefault?: boolean;
  isDone?: boolean;
}

interface StatusCellProps {
  value: any;
  config?: { labels?: StatusLabel[] };
  onChange?: (value: string) => void;
  readOnly?: boolean;
}

const DEFAULT_LABELS: StatusLabel[] = [
  { id: '1', text: '', color: '#c4c4c4', isDefault: true },
  { id: '2', text: 'Working on it', color: '#fdab3d' },
  { id: '3', text: 'Done', color: '#00c875', isDone: true },
  { id: '4', text: 'Stuck', color: '#e2445c' },
];

export const StatusCell: React.FC<StatusCellProps> = ({ value, config, onChange, readOnly }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const labels = config?.labels || DEFAULT_LABELS;
  
  // Parse value if it's a JSON string
  let parsedValue = value;
  try {
    if (typeof value === 'string' && value.startsWith('{')) {
      parsedValue = JSON.parse(value);
    }
  } catch (e) {
    // Keep original value
  }
  
  const selectedLabel = labels.find((l) => 
    l.id === parsedValue?.id || 
    l.id === parsedValue || 
    l.text === parsedValue
  ) || labels[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (label: StatusLabel) => {
    if (onChange && !readOnly) {
      onChange(JSON.stringify({ id: label.id, text: label.text, color: label.color }));
    }
    setIsOpen(false);
  };

  const getContrastColor = (hexColor: string) => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !readOnly && setIsOpen(!isOpen)}
        className="w-full px-3 py-1.5 rounded text-sm font-medium transition-all hover:opacity-80 disabled:cursor-not-allowed"
        style={{
          backgroundColor: selectedLabel.color,
          color: getContrastColor(selectedLabel.color),
        }}
        disabled={readOnly}
      >
        {selectedLabel.text || '-'}
      </button>

      {isOpen && !readOnly && (
        <div className="absolute z-50 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 max-h-80 overflow-y-auto">
          {labels.map((label) => (
            <button
              key={label.id}
              onClick={() => handleSelect(label)}
              className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between group"
            >
              <div className="flex items-center gap-2 flex-1">
                <div
                  className="w-6 h-6 rounded flex items-center justify-center"
                  style={{ backgroundColor: label.color }}
                >
                  {selectedLabel.id === label.id && (
                    <Check className="w-4 h-4" style={{ color: getContrastColor(label.color) }} />
                  )}
                </div>
                <span className="text-sm">{label.text || '(Empty)'}</span>
              </div>
              {label.description && (
                <span className="text-xs text-gray-400 ml-2" title={label.description}>
                  i
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
